import Vue from "vue";
import { Chrome } from "vue-color";
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import {
  getListItemBadgeStyle,
  hexToRgba,
  normalizeHexColor,
} from "@/utils/listItemColor";
import { listValuesInTreeOrder } from "@/utils/listValueTree";

export { normalizeHexColor };

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

/** Clave del campo de banderas de los modelos (el `FLAG_FIELD` del backend). */
export const FLAG_FIELD_KEY = "flag_category";

/** Etiqueta tal cual la escribe el usuario (no se interpreta como id de lista). */
function plainCategoryText(value) {
  if (value == null) return "";
  if (typeof value === "object") return "";
  return String(value).trim();
}

/**
 * Etiqueta del flag. `flag_category` es un campo de tipo list: puede llegar poblado
 * (`{_id,label}`) o como el _id suelto del valor, que se resuelve contra el catálogo
 * (los valores de la lista `flag_category`).
 * @param {*} value
 * @param {Array<{_id?:string,name:string}>} [catalog]
 */
export function normalizeFinancialCategory(value, catalog = []) {
  if (value == null) return "";
  if (Array.isArray(value)) {
    // Varias banderas: la primera con nombre, para los lugares que muestran una sola.
    for (const item of value) {
      const name = normalizeFinancialCategory(item, catalog);
      if (name) return name;
    }
    return "";
  }
  if (typeof value === "object") {
    // El campo `category` de un modelo dinámico puede venir poblado como
    // documento (p.ej. { _id, label, hierarchy }): mostramos su nombre, nunca el JSON.
    const label =
      value.name ?? value.label ?? value.hierarchy ?? value.title ?? value.value;
    if (label != null && typeof label !== "object") {
      return String(label).trim();
    }
    return normalizeFinancialCategory(value._id, catalog);
  }
  const raw = String(value).trim();
  if (!raw) return "";
  if (!OBJECT_ID_RE.test(raw)) return raw;
  const entry = (Array.isArray(catalog) ? catalog : []).find(
    (e) => e && e._id && String(e._id) === raw
  );
  // Sin catálogo cargado no se muestra el id crudo: mejor vacío que un ObjectId.
  return entry ? String(entry.name || "").trim() : "";
}

/**
 * Todas las banderas de un valor guardado. `flag_category` admite varias: en la base
 * es un arreglo de ids de la lista de flags, y en los documentos anteriores a esa
 * mejora es un valor suelto, que se lee igual.
 * @param {*} value
 * @param {Array<{_id?:string,name:string}>} [catalog]
 * @returns {string[]} sin vacíos y sin repetidos
 */
export function normalizeFinancialCategories(value, catalog = []) {
  const raw = Array.isArray(value) ? value : [value];
  const out = [];
  raw.forEach((item) => {
    const name = normalizeFinancialCategory(item, catalog);
    if (!name) return;
    if (out.some((n) => n.toLowerCase() === name.toLowerCase())) return;
    out.push(name);
  });
  return out;
}

/** Swatches de los popups de bandera nueva y de color del reporte de embarques. */
export const FINANCIAL_CATEGORY_COLOR_PRESETS = [
  "#6b7280",
  "#0ea5e9",
  "#f59e0b",
  "#8b5cf6",
  "#14b8a6",
  "#e11d48",
  "#22c55e",
  "#f97316",
  "#2563eb",
  "#db2777",
  "#84cc16",
  "#0891b2",
  "#a16207",
  "#4f46e5",
  "#dc2626",
  "#7c3aed",
  "#0f766e",
  "#ca8a04",
  "#be123c",
  "#334155",
];

function parseCategoryEntry(raw) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const name = String(raw.name ?? raw.label ?? "").trim();
    if (!name) return null;
    return {
      // _id del valor en la lista `flag_category` (lo que guarda el documento).
      _id: raw._id ? String(raw._id) : undefined,
      name,
      color: normalizeHexColor(raw.color) || "",
      // Padre en la lista de banderas: los selectores la muestran con su jerarquía.
      parent: raw.parent ? String(raw.parent) : null,
    };
  }
  const name = normalizeFinancialCategory(raw);
  if (!name) return null;
  return { name, color: "", parent: null };
}

/**
 * Catálogo de banderas en el orden y con la sangría del árbol de /lists (#422).
 * @param {Array} catalog
 * @returns {Array<{ entry: {_id?:string,name:string,color:string,parent:?string}, depth: number }>}
 */
export function flagCatalogInTreeOrder(catalog = []) {
  const entries = (Array.isArray(catalog) ? catalog : [])
    .map(parseCategoryEntry)
    .filter(Boolean);
  return listValuesInTreeOrder(entries, { label: (e) => e.name }).map(
    ({ value, depth }) => ({ entry: value, depth })
  );
}

/**
 * Color del flag guardado en su ítem de la lista `flag_category`. Sin color asignado
 * devuelve '' (el badge se muestra neutro): el color lo elige el admin en el editor de
 * listas, no se deriva del nombre.
 * @param {string} category
 * @param {Array<{name:string,color?:string}|string>} [catalog]
 */
export function resolveFinancialCategoryColor(category, catalog = []) {
  const name = normalizeFinancialCategory(category, catalog);
  if (!name) return "";
  const entries = (Array.isArray(catalog) ? catalog : [])
    .map(parseCategoryEntry)
    .filter(Boolean);
  const found = entries.find((e) => e.name.toLowerCase() === name.toLowerCase());
  return found?.color || "";
}

/** Inline style for a category badge, or null if empty. */
export function getFinancialCategoryBadgeStyle(category, catalog = []) {
  const name = normalizeFinancialCategory(category, catalog);
  if (!name) return null;
  return getListItemBadgeStyle(resolveFinancialCategoryColor(name, catalog));
}

/** Full-row background from category color (soft tint). */
export function getFinancialCategoryRowStyle(category, catalog = []) {
  const name = normalizeFinancialCategory(category, catalog);
  if (!name) return null;
  const bg = resolveFinancialCategoryColor(name, catalog);
  if (!bg) return null;
  const tint = hexToRgba(bg, 0.35);
  return {
    backgroundColor: tint,
    // Sticky/pinned tds need an opaque bg; they read this instead of forcing white.
    "--list-row-bg": tint,
  };
}

export async function fetchFinancialMovementCategories(modelName) {
  const key = String(modelName || "").trim();
  if (!key) return [];
  const { data } = await wencesApi.get("/api/financial/categories", {
    params: { modelName: key },
  });
  if (!data?.success) return [];
  return (data.data || []).map(parseCategoryEntry).filter(Boolean);
}

/**
 * Popup de banderas: elegir una del catálogo o quitar la que tenga. El catálogo es por
 * modelName y una sola bandera por vez: para poner varias está el select múltiple de la
 * tarjeta de la operación.
 *
 * Acá **solo se clasifica**: no se crean banderas ni se les cambia el color (#383). El
 * alta y el color de una bandera se hacen en la lista de banderas (`/lists`) y, en el
 * reporte de embarques, con `openNewFlagPopup` / `openFlagColorPopup`.
 * @returns {Promise<string[]|null>} banderas asignadas ([] = ninguna), o null si se canceló
 */
export async function openFinancialCategoryPopup({
  t,
  currentCategory = "",
  modelName,
  saveUrl,
  saveFn,
  catalog: catalogHint,
}) {
  const translate = typeof t === "function" ? t : (k) => k;
  const contentType = String(modelName || "").trim();
  let categories = [];
  try {
    categories =
      Array.isArray(catalogHint) && catalogHint.length
        ? catalogHint.map(parseCategoryEntry).filter(Boolean)
        : await fetchFinancialMovementCategories(contentType);
  } catch (err) {
    console.error("Error loading financial categories:", err);
  }

  const currentList = normalizeFinancialCategories(currentCategory, categories);
  // Una bandera que ya no está en el catálogo arranca en «sin bandera», como hacía el select.
  const current = categories.some(
    (e) => e.name.toLowerCase() === String(currentList[0] || "").toLowerCase()
  )
    ? currentList[0]
    : "";

  // Lista en lugar de un <select>: así cada bandera lleva la sangría de su nivel y el
  // punto de su color, como en el editor de la lista de banderas (#422).
  const rowHtml = (value, text, { depth = 0, color = "" } = {}) => {
    const selected = value.toLowerCase() === current.toLowerCase();
    const dot = color
      ? `<span class="swal-flag-pick__dot" style="background:${escapeHtml(color)}"></span>`
      : `<span class="swal-flag-pick__dot swal-flag-pick__dot--none"></span>`;
    return `<button type="button" role="option" aria-selected="${selected}"
      class="swal-flag-pick__row${selected ? " is-selected" : ""}"
      data-value="${escapeHtml(value)}"
      style="padding-left:${0.6 + depth * 1.25}rem">${dot}<span>${escapeHtml(text)}</span></button>`;
  };
  const rowsHtml = [
    rowHtml("", translate("No category")),
    ...flagCatalogInTreeOrder(categories).map(({ entry, depth }) =>
      rowHtml(entry.name, entry.name, { depth, color: entry.color })
    ),
  ].join("");

  const result = await Swal.fire({
    title: translate("Category"),
    width: "28rem",
    html: `
      <div class="text-start">
        <style>
          .swal-flag-pick { max-height: 50vh; overflow-y: auto; margin-bottom: 0.5rem;
            border: 1px solid var(--bs-border-color, #dee2e6); border-radius: 0.375rem; }
          .swal-flag-pick__row { display: flex; align-items: center; gap: 0.5rem; width: 100%;
            padding: 0.4rem 0.6rem; border: 0; border-bottom: 1px solid var(--bs-border-color, #dee2e6);
            background: none; color: inherit; font-size: 0.9rem; text-align: left; cursor: pointer; }
          .swal-flag-pick__row:last-child { border-bottom: 0; }
          .swal-flag-pick__row:hover { background: rgba(13, 110, 253, 0.08); }
          .swal-flag-pick__row.is-selected { background: rgba(13, 110, 253, 0.18); font-weight: 600; }
          .swal-flag-pick__dot { flex-shrink: 0; width: 12px; height: 12px; border-radius: 50%;
            border: 1px solid rgba(0, 0, 0, 0.25); }
          .swal-flag-pick__dot--none { border-style: dashed; border-color: #adb5bd; }
        </style>
        <label class="form-label" id="swal-fin-cat-label">${escapeHtml(
          translate("Category")
        )}</label>
        <input id="swal-fin-cat" type="hidden" value="${escapeHtml(current)}" />
        <div class="swal-flag-pick" role="listbox" aria-labelledby="swal-fin-cat-label">${rowsHtml}</div>
        <p class="small text-secondary mb-0">${escapeHtml(
          translate("Flags are created and edited in the flags list")
        )}</p>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: translate("Apply category"),
    cancelButtonText: translate("Cancel"),
    focusConfirm: false,
    didOpen: () => {
      const popup = Swal.getPopup();
      const input = document.getElementById("swal-fin-cat");
      const rows = popup ? [...popup.querySelectorAll(".swal-flag-pick__row")] : [];
      const select = (row) => {
        rows.forEach((r) => {
          const on = r === row;
          r.classList.toggle("is-selected", on);
          r.setAttribute("aria-selected", String(on));
        });
        if (input) input.value = row.getAttribute("data-value") || "";
      };
      rows.forEach((row) => {
        row.addEventListener("click", () => select(row));
        // Doble clic: elegir y aplicar de una vez.
        row.addEventListener("dblclick", () => {
          select(row);
          Swal.clickConfirm();
        });
      });
      const selected = rows.find((r) => r.classList.contains("is-selected"));
      if (selected) {
        selected.scrollIntoView({ block: "nearest" });
        selected.focus();
      }
    },
    preConfirm: () => {
      const input = document.getElementById("swal-fin-cat");
      return { category: plainCategoryText(input ? input.value : "") };
    },
  });

  if (!result.isConfirmed) return null;

  const value = result.value || {};
  const category = plainCategoryText(value.category);
  // El popup asigna una bandera; el contrato con quien guarda sigue siendo de arreglo
  // porque el campo `flag_category` admite varias.
  const picked = category ? [category] : [];

  if (typeof saveFn === "function") {
    await saveFn({
      categories: picked,
      category: picked[0] || "",
      modelName: contentType,
    });
    return picked;
  }

  if (!saveUrl) {
    throw new Error("saveUrl or saveFn is required");
  }

  const body = { categories: picked, category: picked[0] || "" };
  if (contentType) body.modelName = contentType;
  const { data } = await wencesApi.put(saveUrl, body);
  if (!data?.success) {
    throw new Error(data?.error || "Failed to save category");
  }
  const saved = data.data?.assignedCategories;
  if (Array.isArray(saved)) return saved;
  const one = plainCategoryText(data.data?.category);
  return one ? [one] : [];
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function mountCategoryColorWheel(hostEl, initialHex, onHex) {
  if (!hostEl) return null;
  return new Vue({
    data() {
      return { color: normalizeHexColor(initialHex) || "#6b7280" };
    },
    render(h) {
      return h(Chrome, {
        props: {
          value: this.color,
          disableAlpha: true,
        },
        on: {
          input: (val) => {
            this.color = val;
            const hex = normalizeHexColor(val && val.hex);
            if (hex) onHex(hex);
          },
        },
      });
    },
    methods: {
      setHex(hex) {
        const next = normalizeHexColor(hex);
        if (next) this.color = next;
      },
    },
  }).$mount(hostEl);
}

function markSelectedCategorySwatch(popup, hex) {
  const normalized = normalizeHexColor(hex);
  if (!popup) return;
  popup.querySelectorAll(".swal-fin-cat-swatch").forEach((btn) => {
    const isMatch = normalizeHexColor(btn.getAttribute("data-color")) === normalized;
    btn.classList.toggle("is-selected", isMatch);
  });
}

/**
 * Popup del color de una bandera ya existente: la rueda y los presets del popup de
 * banderas, sin elegir bandera. El color es de la bandera en sí (la lista es una sola
 * para todo el sistema), así que se ve igual en todas las pantallas.
 * @returns {Promise<string|null>} hex elegido, o null si se canceló
 */
export async function openFlagColorPopup({ t, name, currentColor = "", catalog = [] }) {
  const translate = typeof t === "function" ? t : (k) => k;
  const flagName = normalizeFinancialCategory(name, catalog);
  if (!flagName) return null;
  const initialColor =
    normalizeHexColor(currentColor) ||
    resolveFinancialCategoryColor(flagName, catalog) ||
    FINANCIAL_CATEGORY_COLOR_PRESETS[0];

  const presetsHtml = FINANCIAL_CATEGORY_COLOR_PRESETS.map(
    (hex) =>
      `<button type="button" class="swal-fin-cat-swatch" data-color="${escapeHtml(
        hex
      )}" style="background:${escapeHtml(
        hex
      )};width:1.35rem;height:1.35rem;border-radius:0.25rem;border:2px solid transparent;padding:0;cursor:pointer;" title="${escapeHtml(
        hex
      )}"></button>`
  ).join("");

  let colorWheelVm = null;

  const result = await Swal.fire({
    title: translate("financialSummary.stages.flagColorTitle").replace("{name}", flagName),
    width: "24rem",
    html: `
      <div class="text-start">
        <style>
          .swal-fin-cat-wheel-wrap { display: flex; justify-content: center; margin: 0 0 0.75rem; }
          .swal-fin-cat-wheel-wrap .vc-chrome { box-shadow: none !important; }
          .swal-fin-cat-swatches { display: flex; flex-wrap: wrap; gap: 0.35rem; justify-content: center; }
          .swal-fin-cat-swatch.is-selected { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0d6efd; }
        </style>
        <input id="swal-flag-color" type="hidden" value="${escapeHtml(initialColor)}" />
        <div class="swal-fin-cat-wheel-wrap"><div id="swal-flag-color-wheel"></div></div>
        <div class="swal-fin-cat-swatches">${presetsHtml}</div>
        <p class="text-muted small mt-2 mb-0">${escapeHtml(
          translate("financialSummary.stages.flagColorHint")
        )}</p>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: translate("financialSummary.stages.flagColorApply"),
    cancelButtonText: translate("Cancel"),
    focusConfirm: false,
    didOpen: () => {
      const popup = Swal.getPopup();
      const colorInput = document.getElementById("swal-flag-color");
      const applyHex = (hex) => {
        const normalized = normalizeHexColor(hex);
        if (!normalized) return;
        if (colorInput) colorInput.value = normalized;
        colorWheelVm?.setHex(normalized);
        markSelectedCategorySwatch(popup, normalized);
      };
      colorWheelVm = mountCategoryColorWheel(
        document.getElementById("swal-flag-color-wheel"),
        initialColor,
        (hex) => {
          if (colorInput) colorInput.value = hex;
          markSelectedCategorySwatch(popup, hex);
        }
      );
      popup?.querySelectorAll(".swal-fin-cat-swatch").forEach((btn) => {
        btn.addEventListener("click", (ev) => {
          ev.preventDefault();
          applyHex(btn.getAttribute("data-color"));
        });
      });
      applyHex(initialColor);
    },
    willClose: () => {
      if (colorWheelVm) {
        colorWheelVm.$destroy();
        colorWheelVm = null;
      }
    },
    preConfirm: () => normalizeHexColor(document.getElementById("swal-flag-color")?.value) || "",
  });

  if (!result.isConfirmed) return null;
  return result.value || null;
}

/**
 * Popup para crear una bandera nueva: nombre + color, con la misma rueda y los mismos
 * presets que el popup del color. No guarda nada: devuelve lo elegido y quien llama
 * la crea y la asigna.
 * @param {{t?:Function, catalog?:Array}} options
 * @returns {Promise<{name:string,color:string}|null>} null si se canceló
 */
export async function openNewFlagPopup({ t, catalog = [] }) {
  const translate = typeof t === "function" ? t : (k) => k;
  const entries = (Array.isArray(catalog) ? catalog : [])
    .map(parseCategoryEntry)
    .filter(Boolean);
  const initialColor = FINANCIAL_CATEGORY_COLOR_PRESETS[0] || "#6b7280";

  const presetsHtml = FINANCIAL_CATEGORY_COLOR_PRESETS.map(
    (hex) =>
      `<button type="button" class="swal-fin-cat-swatch" data-color="${escapeHtml(
        hex
      )}" style="background:${escapeHtml(
        hex
      )};width:1.35rem;height:1.35rem;border-radius:0.25rem;border:2px solid transparent;padding:0;cursor:pointer;" title="${escapeHtml(
        hex
      )}"></button>`
  ).join("");

  let colorWheelVm = null;

  const result = await Swal.fire({
    title: translate("financialSummary.stages.flagCreateTitle"),
    width: "24rem",
    html: `
      <div class="text-start">
        <style>
          .swal-fin-cat-wheel-wrap { display: flex; justify-content: center; margin: 0 0 0.75rem; }
          .swal-fin-cat-wheel-wrap .vc-chrome { box-shadow: none !important; }
          .swal-fin-cat-swatches { display: flex; flex-wrap: wrap; gap: 0.35rem; justify-content: center; }
          .swal-fin-cat-swatch.is-selected { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0d6efd; }
        </style>
        <label class="form-label" for="swal-flag-new-name">${escapeHtml(
          translate("financialSummary.stages.flagCreateNameLabel")
        )}</label>
        <input id="swal-flag-new-name" type="text" class="form-control mb-2" maxlength="80" />
        <input id="swal-flag-new-color" type="hidden" value="${escapeHtml(initialColor)}" />
        <div class="swal-fin-cat-wheel-wrap"><div id="swal-flag-new-wheel"></div></div>
        <div class="swal-fin-cat-swatches">${presetsHtml}</div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: translate("financialSummary.stages.flagCreateApply"),
    cancelButtonText: translate("Cancel"),
    focusConfirm: false,
    didOpen: () => {
      const popup = Swal.getPopup();
      const colorInput = document.getElementById("swal-flag-new-color");
      const applyHex = (hex) => {
        const normalized = normalizeHexColor(hex);
        if (!normalized) return;
        if (colorInput) colorInput.value = normalized;
        colorWheelVm?.setHex(normalized);
        markSelectedCategorySwatch(popup, normalized);
      };
      colorWheelVm = mountCategoryColorWheel(
        document.getElementById("swal-flag-new-wheel"),
        initialColor,
        (hex) => {
          if (colorInput) colorInput.value = hex;
          markSelectedCategorySwatch(popup, hex);
        }
      );
      popup?.querySelectorAll(".swal-fin-cat-swatch").forEach((btn) => {
        btn.addEventListener("click", (ev) => {
          ev.preventDefault();
          applyHex(btn.getAttribute("data-color"));
        });
      });
      applyHex(initialColor);
      document.getElementById("swal-flag-new-name")?.focus();
    },
    willClose: () => {
      if (colorWheelVm) {
        colorWheelVm.$destroy();
        colorWheelVm = null;
      }
    },
    preConfirm: () => {
      const name = plainCategoryText(
        document.getElementById("swal-flag-new-name")?.value
      );
      if (!name) {
        Swal.showValidationMessage(
          translate("financialSummary.stages.flagCreateNameRequired")
        );
        return false;
      }
      if (entries.some((e) => e.name.toLowerCase() === name.toLowerCase())) {
        Swal.showValidationMessage(
          translate("financialSummary.stages.flagCreateNameExists")
        );
        return false;
      }
      const color =
        normalizeHexColor(document.getElementById("swal-flag-new-color")?.value) || "";
      return { name, color };
    },
  });

  if (!result.isConfirmed) return null;
  return result.value || null;
}
